/**
 * Record-level access: the rule definitions.
 *
 * Five independent sources, combined with **OR within a source and AND across
 * constraints** (SECURITY §6.2):
 *
 *   hierarchy scope · ownership · explicit share · document state · Party relationship
 *
 * The first four answer *can this actor reach this record at all?* and are
 * therefore OR-ed: one route in is enough. State answers *may this action be
 * performed on a record in this state?* and is AND-ed, because an Issued invoice
 * is immutable no matter how the actor reached it — `STM-2` makes "who can edit
 * this issued invoice?" answer **nobody** rather than depend on which endpoint
 * was called.
 *
 * Both the SQL predicate and the single-record `can()` are generated from these
 * definitions, so the two evaluation paths cannot disagree.
 */
/**
 * Every resource whose reach is decided by rules rather than by RLS alone.
 *
 * It began as Platform Core only, because no business module existed. Stage 8
 * added the first that does, and the shape `platform.org_node` predicted — a
 * record whose scope node decides who reaches it — is now what every Finance
 * declaration below looks like, with `legal_entity_id` in that position.
 */
export const RESOURCE_ACCESS = [
    {
        resource: 'platform.org_node',
        table: 'tessma.org_node',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'id' },
    },
    {
        resource: 'platform.app_user',
        table: 'tessma.app_user',
        columns: { id: 'id', tenantId: 'tenant_id', owner: 'id' },
        sensitiveFields: { email: 'platform.user.read' },
    },
    {
        resource: 'platform.role_assignment',
        table: 'tessma.role_assignment',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'scope_node_id', owner: 'user_id' },
    },
    {
        resource: 'platform.audit_event',
        table: 'tessma.audit_event',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'scope_node_id' },
        // The audit trail is append-only. No actor may update or delete an event,
        // whatever their role — the grants and policies enforce it at the database,
        // and this makes the same answer available to the application.
        immutableIn: { write: ['*'], delete: ['*'] },
    },
    /**
     * ADDED IN STAGE 6, AND IT CLOSES A GAP RATHER THAN CREATING A RULE.
     *
     * A task has always had a scope node and an assignee; what it did not have was
     * a declaration saying those decide who may reach it. Without one,
     * `accessPredicate('platform.task', …)` throws, and Stage 6 search cannot
     * filter tasks by the same predicate a direct read uses — which is the rule
     * the whole stage rests on.
     *
     * The three reach sources are the ordinary ones and are OR-ed: the task sits
     * in a node your roles cover, OR it is assigned to you, OR somebody shared it
     * with you. `assignee_user_id` as `owner` is what makes "my work" a reach
     * source rather than a filter somebody remembered to apply.
     *
     * NOT DECLARED: a state constraint. A completed task is not immutable — it is
     * still readable, still commentable, and `moveTask` refuses to change a
     * settled one in code for reasons the lifecycle owns. Declaring `immutableIn`
     * here would duplicate that decision in a second place, and two places is how
     * they diverge.
     */
    {
        resource: 'platform.task',
        table: 'tessma.task',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'scope_node_id',
            owner: 'assignee_user_id',
            state: 'status',
        },
    },
    /**
     * ---- Stage 8: Party Master and the shared catalogue ---------------------
     *
     * NEITHER IS SCOPE-BEARING, and that is a decision rather than an omission.
     * A party is canonical identity shared across the tenant (D-2): the same
     * organisation is the same organisation to every branch, and narrowing
     * identity by hierarchy would mean two branches dealing with one company
     * seeing two different companies.
     *
     * What IS narrowed by hierarchy is the FINANCIAL record. `finance.customer`
     * below carries the legal entity, and so does every document. So a scoped
     * user can see that a party exists; what they cannot see is a penny it owes
     * outside their reach, which is exactly what the Stage 8 parity proofs check.
     */
    {
        resource: 'platform.party',
        table: 'tessma.party',
        columns: { id: 'id', tenantId: 'tenant_id', party: 'id' },
    },
    {
        resource: 'platform.catalogue_item',
        table: 'tessma.catalogue_item',
        columns: { id: 'id', tenantId: 'tenant_id' },
    },
    /**
     * ---- Stage 8: Finance ---------------------------------------------------
     *
     * ONE DECLARATION PER READABLE RECORD TYPE, and it is the ONLY statement of
     * who may reach it. Direct reads, lists, search, saved searches, reports,
     * exports, metrics and AI retrieval all compile THESE definitions into SQL,
     * which is why a Finance controller has no visibility logic to write — and
     * why deleting a line here breaks the parity tests rather than only the one
     * screen it came from.
     *
     * THE SCOPE NODE IS THE LEGAL ENTITY. That single choice is the whole
     * legal-entity boundary, expressed in the mechanism the platform already
     * enforces reach with. A user assigned at the group node reaches both
     * entities; a user assigned into one entity's subtree reaches one. Reach is
     * therefore NEVER inferred from tenant membership: it is the ordinary
     * hierarchy predicate applied to `legal_entity_id`, and a tenant member
     * holding no assignment covering that node reaches nothing at all.
     *
     * `immutableIn` repeats, in the application, what 0056-0060 already enforce
     * with triggers. The duplication is deliberate: the trigger is the
     * guarantee, and this is what lets the API answer 403 with a sentence
     * instead of surfacing a database exception as a 500.
     */
    {
        resource: 'finance.account',
        table: 'tessma.finance_account',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'legal_entity_id' },
    },
    {
        resource: 'finance.period',
        table: 'tessma.finance_period',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'legal_entity_id', state: 'status' },
        immutableIn: { write: ['closed', 'locked'], delete: ['*'] },
    },
    {
        resource: 'finance.journal',
        table: 'tessma.finance_journal',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
        },
        immutableIn: { write: ['posted', 'reversed'], delete: ['*'] },
    },
    {
        resource: 'finance.ledger_entry',
        table: 'tessma.finance_ledger_entry',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'legal_entity_id' },
        immutableIn: { write: ['*'], delete: ['*'] },
    },
    {
        resource: 'finance.customer',
        table: 'tessma.finance_customer',
        columns: {
            id: 'party_id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            party: 'party_id',
        },
    },
    {
        resource: 'finance.supplier',
        table: 'tessma.finance_supplier',
        columns: {
            id: 'party_id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            party: 'party_id',
        },
        sensitiveFields: { bankAccountLast4: 'finance.supplier.bank_details' },
    },
    {
        resource: 'finance.invoice',
        table: 'tessma.finance_invoice',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
            party: 'party_id',
        },
        immutableIn: { write: ['issued', 'part_paid', 'paid', 'void'], delete: ['*'] },
    },
    {
        resource: 'finance.credit_note',
        table: 'tessma.finance_credit_note',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
            party: 'party_id',
        },
        immutableIn: { write: ['issued', 'allocated', 'void'], delete: ['*'] },
    },
    {
        resource: 'finance.receipt',
        table: 'tessma.finance_receipt',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
            party: 'party_id',
        },
        immutableIn: { write: ['posted', 'void'], delete: ['*'] },
    },
    {
        resource: 'finance.bill',
        table: 'tessma.finance_bill',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
            party: 'party_id',
        },
        immutableIn: { write: ['posted', 'part_paid', 'paid', 'void'], delete: ['*'] },
    },
    {
        resource: 'finance.supplier_credit',
        table: 'tessma.finance_supplier_credit',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
            party: 'party_id',
        },
        immutableIn: { write: ['posted', 'allocated', 'void'], delete: ['*'] },
    },
    {
        resource: 'finance.payment',
        table: 'tessma.finance_payment',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
            party: 'party_id',
        },
        immutableIn: { write: ['posted', 'void'], delete: ['*'] },
    },
    {
        resource: 'finance.bank_account',
        table: 'tessma.finance_bank_account',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'legal_entity_id' },
    },
    {
        resource: 'finance.bank_transaction',
        table: 'tessma.finance_bank_transaction',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            state: 'match_state',
        },
    },
    {
        resource: 'finance.reconciliation',
        table: 'tessma.finance_reconciliation',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
            state: 'status',
        },
        immutableIn: { write: ['completed'], delete: ['*'] },
    },
    /**
     * A MATCH IS A READABLE RECORD IN ITS OWN RIGHT, so it declares its own
     * rules rather than borrowing the reconciliation's.
     *
     * It has to: a SUGGESTION has no reconciliation yet (`reconciliation_id IS
     * NULL`), so a predicate compiled against the reconciliation's table would
     * have had nothing to attach to — and a query that tried was rejected
     * outright by PostgreSQL, which is the better way to learn this.
     *
     * There is no `immutableIn` here. The database's own trigger decides what may
     * move inside a completed reconciliation, and it permits exactly one thing:
     * an attributed, reasoned unmatch. Repeating a weaker version of that rule
     * here would be a second, disagreeing answer.
     */
    {
        resource: 'finance.reconciliation_match',
        table: 'tessma.finance_reconciliation_match',
        columns: {
            id: 'id',
            tenantId: 'tenant_id',
            scopeNode: 'legal_entity_id',
            owner: 'created_by',
        },
    },
    /**
     * A VAT PERIOD IS A READABLE RECORD, so it declares its own rules.
     *
     * Its state is `immutableIn` for a reason the return's is not: a submitted
     * period is closed to new transactions, and the application answering "you
     * may not change that" with a sentence is better than the database answering
     * it with a PL/pgSQL message. The trigger remains the guarantee.
     */
    {
        resource: 'finance.vat_period',
        table: 'tessma.finance_vat_period',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'legal_entity_id', state: 'status' },
        immutableIn: { write: ['submitted', 'locked'], delete: ['*'] },
    },
    {
        resource: 'finance.vat_return',
        table: 'tessma.finance_vat_return',
        columns: { id: 'id', tenantId: 'tenant_id', scopeNode: 'legal_entity_id', state: 'status' },
        immutableIn: { write: ['submitted'], delete: ['*'] },
    },
];
/**
 * `platform.attachment` is deliberately ABSENT from the list above.
 *
 * An attachment has no access rules of its own. It is reachable exactly when
 * the record it is attached to is reachable, and `@tessma/attachments` derives
 * that from the parent on every read by ANDing the PARENT's predicate into the
 * query.
 *
 * The alternative — copying the parent's scope node onto the attachment row and
 * filtering on the copy — was built first and then removed, because a copy goes
 * stale the moment the parent record moves between hierarchy nodes, and an
 * access decision made on a stale copy is a decision made on what used to be
 * true. Registering the resource here would make that mistake available again:
 * `accessPredicate('platform.attachment', …)` would silently compile a filter
 * on the attachment's own columns. It throws `UnknownResourceError` instead,
 * which is the fail-closed answer, and an integration test asserts it.
 *
 * The copied node survives as `scope_node_id_at_attach`: a diagnostic, named so
 * it cannot be mistaken for an authority.
 */
/**
 * A declaration error that would make the two evaluation paths disagree.
 *
 * The SQL predicate can only AND a state constraint if there is a state column
 * to compare; `can()` reads the state off the record and would enforce it
 * anyway. A definition that names states without naming the column would
 * therefore be stricter in memory than in the database — the precise divergence
 * this module exists to prevent — so it is rejected at load rather than left to
 * be discovered by a leak.
 */
export class InvalidAccessDefinitionError extends Error {
    constructor(resource, problem) {
        super(`Access definition for ${JSON.stringify(resource)} is invalid: ${problem}`);
        this.name = 'InvalidAccessDefinitionError';
    }
}
/** Throws unless the definition can be enforced identically on both paths. */
export function validateAccessDefinition(definition) {
    for (const [action, states] of Object.entries(definition.immutableIn ?? {})) {
        const stateSpecific = (states ?? []).filter((state) => state !== '*');
        if (stateSpecific.length > 0 && definition.columns.state === undefined) {
            throw new InvalidAccessDefinitionError(definition.resource, `immutableIn.${action} names states (${stateSpecific.join(', ')}) but the resource ` +
                'declares no state column, so the SQL predicate could not enforce them.');
        }
    }
}
// Checked at load, so a bad declaration cannot reach a running system.
for (const definition of RESOURCE_ACCESS)
    validateAccessDefinition(definition);
const BY_RESOURCE = new Map(RESOURCE_ACCESS.map((definition) => [definition.resource, definition]));
export function resourceAccessDefinition(resource) {
    return BY_RESOURCE.get(resource);
}
export class UnknownResourceError extends Error {
    constructor(resource) {
        super(`Resource ${JSON.stringify(resource)} has no access definition. Declare it in ` +
            'packages/platform/authz/src/access-rules.ts — a resource with no rules is a resource ' +
            'with no access control, and the compiler refuses to guess one.');
        this.name = 'UnknownResourceError';
    }
}
/**
 * Is this action refused on a record in this state, to everyone?
 *
 * `'*'` means every state — used for genuinely append-only resources.
 */
export function isImmutable(definition, action, state) {
    const states = definition.immutableIn?.[action];
    if (states === undefined)
        return false;
    if (states.includes('*'))
        return true;
    return state !== undefined && states.includes(state);
}
//# sourceMappingURL=access-rules.js.map